import {ValueFindings} from "../findings-generator.component";
import {AnalysisGenerationConfig} from "../../../../analysis";
import {replaceAnchors} from "../../../../../../tools/replace-anchors/replace-anchors";
import {formatExternalFindings} from "../format/format-external-findings";
import {ExternalFindings} from "../../../external-findings";
import {CardiologsSentenceSelector} from "./cardiologs-sentence-selector";
import {CasSentenceSelector} from "./cas-sentence-selector";
import {calculatePrevailingRhythm} from "./calculate-prevailing-rhythm";
import {ConfigField, SentenceSelector} from "./sentence-selector";
import {ExternalAnalysisType} from "../../../../../hospitals/edit-hospital/hospital.details";

export function generateFindings(
  {
    externalFindings,
    externalAnalysisType,
    analysisGenerationConfig,
    previousFindings,
    locale
  }: {
    externalFindings: ExternalFindings;
    externalAnalysisType: ExternalAnalysisType;
    analysisGenerationConfig: AnalysisGenerationConfig;
    previousFindings: ValueFindings;
    locale: Intl.LocalesArgument;
  }
): ValueFindings {
  const formattedExternalFindings = formatExternalFindings(externalFindings, locale);

  const selector = createSentenceSelector(externalAnalysisType, externalFindings);

  return {
    prevailingRhythm: {
      value: calculatePrevailingRhythm(externalFindings.afibBurden),
      text: formatSentence(selector.selectPrevailingRhythmSentence())
    },
    averageHeartRate: {
      value: formatValue(analysisGenerationConfig.findings_averageHeartRate_value),
      text: formatSentence(selector.selectAverageHeartRateSentence())
    },
    minimumHeartRate: {
      value: formatValue(analysisGenerationConfig.findings_minimumHeartRate_value),
      text: formatSentence(selector.selectMinimumHeartRateSentence())
    },
    maximumHeartRate: {
      value: formatValue(analysisGenerationConfig.findings_maximumHeartRate_value),
      text: formatSentence(selector.selectMaximumHeartRateSentence())
    },
    pausesOverThreshold: {
      value: formatValue(analysisGenerationConfig.findings_pausesOverThreshold_value),
      text: formatSentence(selector.selectSentenceForPausesOverThreshold())
    },
    ventricularBeats: {
      value: formatValue(analysisGenerationConfig.findings_ventricularBeats_value),
      text: formatSentence(selector.selectSentenceForVentricularBeats())
    },
    ventricularRuns: {
      value: formatValue(analysisGenerationConfig.findings_ventricularRuns_value),
      text: formatSentence(selector.selectSentenceForVentricularRuns())
    },
    ventricularTachycardias: {
      value: formatValue(analysisGenerationConfig.findings_ventricularTachycardias_value),
      text: formatSentence(selector.selectSentenceForVentricularTachycardias())
    },
    slowVentricularRhythms: {
      value: formatValue(analysisGenerationConfig.findings_slowVentricularRhythms_value),
      text: formatSentence(selector.selectSentenceForSlowVentricularRhythms())
    },
    atrialBeats: {
      value: formatValue(analysisGenerationConfig.findings_atrialBeats_value),
      text: formatSentence(selector.selectSentenceForAtrialBeats())
    },
    supraventricularRuns: {
      value: formatValue(analysisGenerationConfig.findings_supraventricularRuns_value),
      text: formatSentence(selector.selectSentenceForSupraventricularRuns())
    },
    atrialTachycardias: {
      value: formatValue(analysisGenerationConfig.findings_atrialTachycardias_value),
      text: formatSentence(selector.selectSentenceForAtrialTachycardias())
    },
    commentAtrial: {
      value: undefined,
      text: formatSentence(selector.selectSentenceForCommentAtrial())
    },
    commentVentricular: {
      value: undefined,
      text: formatSentence(selector.selectSentenceForCommentVentricular())
    },
    symptomDiaryComment: {
      value: undefined,
      text: previousFindings.symptomDiaryComment.text
    }
  };

  function formatValue(sentence: string | undefined) {
    return replaceAnchors(sentence, formattedExternalFindings, "?");
  }

  function formatSentence(fields: ConfigField[]): string | undefined {
    const sentence = fields
      .map(field => analysisGenerationConfig[field])
      .filter(value => value !== undefined)
      .join(". ");

    if (!sentence) {
      return sentence;
    }

    return replaceAnchors(sentence, formattedExternalFindings) + " ";
  }
}

function createSentenceSelector(
  externalAnalysisType: ExternalAnalysisType,
  externalFindings: ExternalFindings
): SentenceSelector {
  if (externalAnalysisType === "CAS") {
    return new CasSentenceSelector(externalFindings);
  }

  return new CardiologsSentenceSelector(externalFindings);
}


